import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { LangSwitch } from "@/components/LangSwitch";
import { ChevronDown, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { LOGO } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  ["/tienda", "Tienda"],
  ["/tienda?line=complementos", "Complementos"],
  ["/tienda?line=salud", "Salud e higiene"],
  ["/cuadra", "Mi cuadra"],
  ["/club", "Club hípico"],
  ["/concursos", "Concursos"],
  ["/adopcion", "Adopción"],
  ["/blog", "Blog"],
  ["/gear", "FAUNNY Gear"],
];
const TOOLS = [
  ["/calculadora", "Calculadora de dieta"],
  ["/peso", "Peso del caballo"],
  ["/diagnostico", "Diagnóstico"],
  ["/guia", "Guía de salud"],
  ["/antidopaje", "Antidopaje FEI"],
  ["/asesor", "Asesor IA"],
];
const MARKET = [
  ["/mercado", "Caballos", "caballos"],
  ["/mercado/guarnicioneria", "Guarnicionería y accesorios", "guarnicioneria"],
  ["/mercado/vans", "Vans y camiones", "vans"],
];
const slug = (l) => `nav-${l.toLowerCase().replace(/\s+/g, "-")}`;
const navCls = (to) => `font-display text-[13px] xl:text-[14px] font-bold whitespace-nowrap hover:text-[#DC2626] transition-colors ${to === "/gear" ? "text-[#DC2626]" : "text-[#0B1D3A]"}`;

const DropMenu = ({ label, items, id, active }) => (
  <div className="relative group" data-testid={`nav-${id}`}>
    <button type="button" className={`${navCls("")} inline-flex items-center gap-1 ${active ? "!text-[#DC2626]" : ""}`} data-testid={`nav-${id}-button`}>{label} <ChevronDown size={14} /></button>
    <div className="absolute left-0 top-full pt-2 hidden group-hover:block group-focus-within:block z-50">
      <div className="bg-white border border-slate-200 rounded shadow-xl py-2 w-60">
        {items.map(([to, l, key]) => <NavLink key={to} to={to} end data-testid={key ? `nav-${id}-${key}` : slug(l)} className="block px-4 py-2 font-display text-sm font-bold text-[#0B1D3A] hover:bg-[#EFF6FF] hover:text-[#DC2626]">{l}</NavLink>)}
      </div>
    </div>
  </div>
);

export const Header = () => {
  const { count, setOpen, settings } = useCart();
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [typing, setTyping] = useState(false);

  useEffect(() => {
    const isField = (t) => t.matches?.("input:not([type=checkbox]):not([type=radio]):not([type=file]), textarea, select") && !t.closest("header");
    const on = (e) => isField(e.target) && window.innerWidth < 1024 && setTyping(true);
    const off = (e) => isField(e.target) && setTyping(false);
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); };
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobile]);

  const submit = (e) => {
    e.preventDefault();
    nav(`/tienda?q=${encodeURIComponent(q)}`);
    setMobile(false);
  };

  return (
    <header className={`sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200 transition-transform duration-200 ${typing ? "-translate-y-full" : ""}`} data-testid="site-header">
      <div className="bg-[#0B1D3A] text-white text-[11px] sm:text-xs tracking-wide">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex justify-between gap-4">
          {pathname.startsWith("/adopcion")
            ? <span data-testid="topbar-adopt">FAUNNY Adopta · Solo adopción responsable</span>
            : <span data-testid="topbar-shipping">Envío gratis desde {Number(settings.free_shipping_from).toLocaleString("es-ES")} € · Pago seguro con tarjeta o Bizum</span>}
          <LangSwitch />
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-4">
        <button className="lg:hidden p-2 -ml-2" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-button" aria-label="Menú">
          {mobile ? <X size={22} /> : <Menu size={22} />}
        </button>
        <Link to="/" data-testid="header-logo-link" className="shrink-0">
          <img src={LOGO} alt="FAUNNY" className="h-12 sm:h-14 w-auto py-0.5" />
        </Link>
        <nav className="hidden lg:flex items-center gap-3 xl:gap-4 ml-4">
          {NAV.slice(0, 3).map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={slug(label)} className={navCls(to)}>{label}</NavLink>
          ))}
          <DropMenu label="Herramientas" items={TOOLS} id="tools" />
          {NAV.slice(3, 5).map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={slug(label)} className={navCls(to)}>{label}</NavLink>
          ))}
          <DropMenu label="Mercado" items={MARKET} id="mercado" active={pathname.startsWith("/mercado")} />
          {NAV.slice(5).map(([to, label]) => (
            <NavLink key={to} to={to} data-testid={slug(label)} className={navCls(to)}>{label}</NavLink>
          ))}
        </nav>
        <form onSubmit={submit} className="hidden md:flex ml-auto items-center border-2 border-slate-400 rounded-full px-3 h-10 w-40 xl:w-48 focus-within:border-[#2563EB]">
          <Search size={16} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto…" data-testid="header-search-input"
            className="field-bare ml-2 text-base text-slate-900 outline-none bg-transparent w-full" />
        </form>
        <div className="ml-auto md:ml-2 flex items-center gap-1">
          <Link to={user ? (user.role === "admin" ? "/admin" : "/cuenta") : "/login"} className="p-2 hover:text-[#DC2626] transition-colors" data-testid="header-account-link" aria-label="Cuenta">
            <User size={22} />
          </Link>
          <button onClick={() => setOpen(true)} className="relative p-2 hover:text-[#DC2626] transition-colors" data-testid="header-cart-button" aria-label="Carrito">
            <ShoppingBag size={22} />
            {count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-0.5 -right-0.5 bg-[#DC2626] text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full grid place-items-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 pb-24 max-h-[calc(100dvh-7.5rem)] overflow-y-auto overscroll-contain" data-testid="mobile-menu">
          <form onSubmit={submit} className="flex items-center border-2 border-slate-400 rounded-full px-3 h-12 my-4 focus-within:border-[#2563EB]">
            <Search size={16} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto…" data-testid="mobile-search-input"
              className="field-bare ml-2 text-base text-slate-900 outline-none w-full" />
          </form>
          {[...NAV.slice(0, 3), ...TOOLS, ...NAV.slice(3, 5), ...MARKET.map(([to, l]) => [to, `Mercado · ${l}`]), ...NAV.slice(5)].map(([to, label]) => (
            <Link key={to} to={to} onClick={() => setMobile(false)} className="block py-2.5 font-display text-xl font-bold text-[#0B1D3A] border-b border-slate-100">
              {label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
